import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Input,
  TablePagination,
  Typography,
  Stack,
  Box,
  Paper,
} from "@mui/material";
import { AddNewPlantNoteDialog } from "popup/AddNewPlantNoteDialog";
import NoData from "components/NoData";
import { APIStatusDialog } from "components/APIStatusDialog";
import { I18n } from "@aws-amplify/core";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
const formatTimestamp = (timestamp) =>
  dayjs(timestamp).format("YYYY-MM-DD HH:mm:ss");

export const EventHistoryTable = ({
  eventState,
  resourcePrefPage,
  rowsPerResourcePrefPage,
  setResourcePrefPage,
  setRowsPerResourcePrefPage,
  account_number,
  endDate,
  startDate,
  dispatchProcessPlant,
  setStartDate,
  setEndDate,
}) => {
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const [tableParentIsMD, setTableParentIsMD] = useState(false);
  // const toolbarRef = useRef(null);
  useEffect(() => {
    //   const parentElement = toolbarRef.current;
    //   if (!parentElement) {
    //     console.error("toolbarRef is not correctly attached to any DOM element.");
    //     return;
    //   }
    const checkSize = (width) => {
      const breakpoint = 960; // 960px is md
      setTableParentIsMD(width < breakpoint);
    };
    //   const resizeObserver = new ResizeObserver((entries) => {
    //     for (let entry of entries) {
    //       checkSize(entry.contentRect.width);
    //     }
    //   });
    //   resizeObserver.observe(parentElement);
    //   checkSize(parentElement.offsetWidth);
    //   return () => {
    //     resizeObserver.disconnect();
    //   };
  }, []);

  const [openAddDialog, setOpenAddDialog] = useState(false);

  const columns = useMemo(() => {
    return [
      {
        field: "timestamp",
        headerName: I18n.get("Timestamp"),
        width: 300,
        renderCell: (params) => formatTimestamp(params.value),
      },
      {
        field: "process",
        headerName: I18n.get("Process"),
        width: 300,
      },
      {
        field: "old_value",
        headerName: I18n.get("Old Value"),
        width: 600,
      },
      {
        field: "new_value",
        headerName: I18n.get("New Value"),
        width: 600,
      },
      {
        field: "executor",
        headerName: I18n.get("Executor"),
        width: 200,
      },
    ];
  }, []);

  if (!eventState.data || eventState.data.length === 0) {
    return (
      <Card>
        <CardContent
          sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
        >
          <Stack
            spacing={2}
            direction={tableParentIsMD ? "column" : "row"}
            alignItems="center"
            sx={{ pb: 2 }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={I18n.get("Start Date")}
                value={startDate}
                onChange={(newValue) => {
                  if (newValue) {
                    setStartDate(dayjs(newValue).startOf("day")); // Imposta l'orario a 00:00:00
                  }
                }}
                format="YYYY/MM/DD"
                maxDate={dayjs()}
                renderInput={(params) => (
                  <Input {...params} fullWidth={tableParentIsMD} />
                )}
              />

              <DatePicker
                label={I18n.get("End Date")}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="YYYY/MM/DD"
                maxDate={dayjs()}
                renderInput={(params) => (
                  <Input {...params} fullWidth={tableParentIsMD} />
                )}
              />
            </LocalizationProvider>
            <Box
              sx={{
                ml: 2,
                flexGrow: 1,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Typography variant="h5">{I18n.get("Event History")}</Typography>
            </Box>
          </Stack>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <NoData />
          </Box>
        </CardContent>
        <AddNewPlantNoteDialog
          open={openAddDialog}
          onClose={() => setOpenAddDialog(false)}
          accountNumber={account_number}
          dispatchProcessPlant={dispatchProcessPlant}
          startDate={startDate}
          endDate={endDate}
        />
        <APIStatusDialog
          open={apiStatus.open}
          status={apiStatus.status}
          message={apiStatus.message}
        />
      </Card>
    );
  }

  return (
    <Card>
      <CardContent
        sx={{ padding: "1rem", "&:last-child": { padding: "1rem" } }}
      >
        <Stack
          spacing={2}
          direction={tableParentIsMD ? "column" : "row"}
          alignItems="center"
          paddingBottom="1rem"
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={I18n.get("Start Date")}
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              format="YYYY/MM/DD"
              maxDate={dayjs()}
              renderInput={(params) => (
                <Input {...params} fullWidth={tableParentIsMD} />
              )}
            />
            <DatePicker
              label={I18n.get("End Date")}
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              format="YYYY/MM/DD"
              maxDate={dayjs()}
              renderInput={(params) => (
                <Input {...params} fullWidth={tableParentIsMD} />
              )}
            />
          </LocalizationProvider>
          <Box
            sx={{
              ml: 2,
              flexGrow: 1,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Typography variant="h5">{I18n.get("Event History")}</Typography>
          </Box>
        </Stack>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell key={column.field}>{column.headerName}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {eventState.data
                .slice(
                  resourcePrefPage * rowsPerResourcePrefPage,
                  resourcePrefPage * rowsPerResourcePrefPage +
                    rowsPerResourcePrefPage
                )
                .map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {columns.map((column, colIndex) => (
                      <TableCell key={colIndex} style={{ padding: "0px" }}>
                        {column.field === "timestamp" && row[column.field]
                          ? dayjs(row[column.field]).format(
                              "YYYY-MM-DD HH:mm:ss"
                            )
                          : row[column.field] !== undefined &&
                            row[column.field] !== null
                          ? row[column.field]
                          : ""}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={eventState.data.length}
          page={resourcePrefPage}
          onPageChange={(event, newPage) => setResourcePrefPage(newPage)}
          rowsPerPage={rowsPerResourcePrefPage}
          onRowsPerPageChange={(event) => {
            setRowsPerResourcePrefPage(parseInt(event.target.value, 10));
            setResourcePrefPage(0);
          }}
          rowsPerPageOptions={[]}
        />
      </CardContent>
      <APIStatusDialog
        open={apiStatus.open}
        status={apiStatus.status}
        message={apiStatus.message}
      />
      <AddNewPlantNoteDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        accountNumber={account_number}
        dispatchProcessPlant={dispatchProcessPlant}
        startDate={startDate}
        endDate={endDate}
      />
    </Card>
  );
};
